import { Button, Card, createSortableColumn, DataTable, type TanstackReactTable } from '@devhop/ui';
import { Eye } from 'lucide-react';
import type { PromotionHistoryListItem } from '@/modules/hr/promotion/domain/types';

type Props = {
    items: PromotionHistoryListItem[];
    isLoading?: boolean;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    onSortingChange?: (id: string, desc: boolean) => void;
    onViewDetail?: (row: PromotionHistoryListItem) => void;
};

export function PromotionTimeline({
    items,
    isLoading,
    sortBy = 'promotedAt',
    sortOrder = 'desc',
    onSortingChange,
    onViewDetail,
}: Props) {
    const columns: TanstackReactTable.ColumnDef<PromotionHistoryListItem>[] = [
        createSortableColumn<PromotionHistoryListItem>(
            'employeeName',
            'ພະນັກງານ',
            {
                size: 180,
                cell: ({ row }) => (
                    <span className="font-medium">
                        {row.original.employeeName}
                    </span>
                ),
            },
        ),
        createSortableColumn<PromotionHistoryListItem>(
            'promotedAt',
            'ວັນທີ',
            {
                size: 120,
                cell: ({ row }) => (
                    <span className="text-muted-foreground">
                        {new Date(
                            row.original.promotedAt,
                        ).toLocaleDateString('en-GB')}
                    </span>
                ),
            },
        ),
        createSortableColumn<PromotionHistoryListItem>(
            'toPositionTitle',
            'ຕຳແໜ່ງ',
            {
                size: 220,
                cell: ({ row }) =>
                    `${row.original.fromPositionTitle ?? '–'} → ${row.original.toPositionTitle}`,
            },
        ),
        createSortableColumn<PromotionHistoryListItem>(
            'toLevelName',
            'ລະດັບ',
            {
                size: 200,
                cell: ({ row }) =>
                    `${row.original.fromLevelName ?? '–'} → ${row.original.toLevelName ?? '–'}`,
            },
        ),
        {
            id: 'reason',
            header: 'ເຫດຜົນ',
            cell: ({ row }) => (
                <span className="text-muted-foreground">
                    {row.original.reason ?? '–'}
                </span>
            ),
            size: 200,
        },
        ...(onViewDetail
            ? [
                  {
                      id: 'actions',
                      header: '',
                      size: 80,
                      cell: ({
                          row,
                      }: {
                          row: { original: PromotionHistoryListItem };
                      }) => (
                          <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onViewDetail(row.original)}
                              title="ລາຍລະອຽດ"
                              aria-label="ລາຍລະອຽດ"
                          >
                              <Eye className="h-4 w-4" />
                          </Button>
                      ),
                  },
              ]
            : []),
    ];

    return (
        <Card>
            <DataTable<PromotionHistoryListItem, unknown>
                noDataMessage="ບໍ່ມີຂໍ້ມູນປະຫວັດ"
                isLoading={isLoading}
                columns={columns}
                data={items}
                offset={0}
                limit={items.length}
                totalCount={items.length}
                onPaginationChange={() => {}}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSortingChange={(sorting) => {
                    const s = sorting[0];
                    if (s?.id && onSortingChange)
                        onSortingChange(s.id, !!s.desc);
                }}
            />
        </Card>
    );
}
